/*
 |--------------------------------------------------------------------------
 | Helpers
 |--------------------------------------------------------------------------
 */

import _ from "lodash";

const { DateTime, Settings } = require("luxon");
Settings.defaultLocale = window.Laravel.app.locale;
window.DateTime = DateTime;

/*
 |--------------------------------------------------------------------------
 | String Helpers
 |--------------------------------------------------------------------------
 */

/** Capitalize */
String.prototype.capitalize = function () {
  return this.charAt(0).toUpperCase() + this.slice(1);
};

/** Phone Format */
String.prototype.phoneFormat = function () {
  return `(${this.slice(0, 3)}) ${this.slice(3, 6)}-${this.slice(6, 10)}`;
};

/** Truncate Strings */
String.prototype.trunc = String.prototype.trunc ||
  function (n) {
    return (this.length > n) ? this.substr(0, n - 1) + "..." : this;
  };

/*
 |--------------------------------------------------------------------------
 | Number Helpers
 |--------------------------------------------------------------------------
 */

/** Number Format */
Number.prototype.format = function (c, d, t) {
  let n = this;
  c = isNaN(c = Math.abs(c)) ? 2 : c;
  d = d == undefined ? "." : d;
  t = t == undefined ? "," : t;
  let s = n < 0 ? "-" : "";
  let i = String(parseInt(n = Math.abs(Number(n) || 0).toFixed(c)));
  let j = (j = i.length) > 3 ? j % 3 : 0;
  return s + (j ? i.substr(0, j) + t : "") + i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + t) + (c ? d + Math.abs(n - i).toFixed(c).slice(2) : "");
};

/** Percentage Format */
Number.prototype.percentageFormat = function (n = 0) {
  return (this * 100).format(n, ",", " ") + "%";
};

/** Price Format */
const currencyLabel = function (iso) {
  const normalizedIso = (iso || "CAD").toUpperCase();
  const currencyLabels = {
    CAD: "$CAD",
    USD: "$USD",
    EUR: "\u20ac",
  };

  return currencyLabels[normalizedIso] || normalizedIso;
};

Number.prototype.priceFormat = function (iso = "CAD") {
  return this.format(2, ",", "\xa0") + "\xa0" + currencyLabel(iso);
};

/**
 * Unit cost format: like priceFormat, but keeps the decimals of a cost under
 * a cent (up to 6), e.g. "0,002 $CAD" for a screw bought by the box.
 */
Number.prototype.unitPriceFormat = function (iso = "CAD") {
  const decimals = (Number(this).toFixed(6).split(".")[1] || "").replace(/0+$/, "").length;

  return this.format(Math.max(2, decimals), ",", "\xa0") + "\xa0" + currencyLabel(iso);
};

/** Number size format */
Number.prototype.fileSizeFormat = function () {
  let i = Math.floor(Math.log(this) / Math.log(1024));
  return (this / Math.pow(1024, i)).toFixed(2) * 1 + " " + ["B", "kB", "MB", "GB", "TB"][i];
};

/*
 |--------------------------------------------------------------------------
 | Date Helpers
 |--------------------------------------------------------------------------
 */

/** String to date */
window.stringToDate = function (date) {
  let s = date;
  let a = s.split(/[^0-9]/);
  return new Date(a[0], a[1] - 1, a[2], 0, 0, 0);
};

/** Date to String */
Date.prototype.format = function () {
  let date = new Date(this.getTime());
  let month = "" + (date.getMonth() + 1),
    day = "" + date.getDate(),
    year = date.getFullYear();

  if (month.length < 2) month = "0" + month;
  if (day.length < 2) day = "0" + day;

  return [year, month, day].join("-");
};

/*
 |--------------------------------------------------------------------------
 | Misc
 |--------------------------------------------------------------------------
 */

/** Deep copy */
window.deepCopy = function (o) {
  return JSON.parse(JSON.stringify(o));
};

/** UUID Generator */
function uuid(n = 12) {
  let text = "";
  let possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

  for (let i = 0; i < n; i++)
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  return text;
}

window.uuid = uuid;

/** Currency converter */
function convertCurrency(amount, fromIso, toIso, rate = 1) {
  const baseIso = window.Laravel?.base_currency || "CAD";
  const normalizedFromIso = (fromIso || baseIso).toUpperCase();
  const normalizedToIso = (toIso || baseIso).toUpperCase();

  if (normalizedFromIso === normalizedToIso) {
    return amount;
  }

  const getCurrencyRate = function (iso, fallbackRate = null) {
    if (iso === baseIso) {
      return 1;
    }

    const foundCurrency = window.Laravel?.currency_rates?.find(currency => currency.id === iso);

    return foundCurrency?.rate || fallbackRate || 1;
  };

  const fromRate = getCurrencyRate(normalizedFromIso);
  const toRate = getCurrencyRate(normalizedToIso, rate);

  return parseFloat(((amount * fromRate) / toRate).toFixed(2));
}

/*
 |--------------------------------------------------------------------------
 | Misc
 |--------------------------------------------------------------------------
 */

window.getColorNameById = function (id) {
  let color = Laravel.colors[id];
  if (color === this.undefined) {
    return null;
  }

  return _.get(color, "label." + Laravel.app.locale, color.code);
};

window.getFinitionNameById = function (id) {
  let finition = Laravel.finitions[id];
  if (finition === this.undefined) {
    return null;
  }

  return _.get(finition, "name." + Laravel.app.locale, finition.shortcut);
};

/**
 * Get base parameter without delimiter suffix
 * @param {String} parameter
 * @returns {String|null}
 */
window.getBaseParameter = function (parameter) {
  if (!parameter) {
    return null;
  }
  return parameter.split(window.Laravel.erp.rule_id_delimiter)[0];
};

/**
 * Check if parameter is color
 *
 * @param {String} parameter
 * @returns {Boolean}
 */
window.isColorParameter = function (parameter) {
  const baseParameter = window.getBaseParameter(parameter);

  if (!baseParameter) {
    return false;
  }

  return window.Laravel.erp.parameter_groups.couleur.includes(baseParameter);
};

window.parameterType = function (parameter) {
  if (window.isNumericParameter(parameter)) {
    return "numeric";
  }

  if (window.isFinitionParameter(parameter)) {
    return "finition";
  }

  if (window.isColorParameter(parameter)) {
    return "color";
  }

  return "option";
};

/**
 * Check if parameter is finition
 *
 * @param {String} parameter
 * @returns {Boolean}
 */
window.isFinitionParameter = function (parameter) {
  const baseParameter = window.getBaseParameter(parameter);

  if (!baseParameter) {
    return false;
  }

  return window.Laravel.erp.parameter_groups.fini.includes(baseParameter);
};

/**
 * Check if parameter is numeric
 *
 * @param {String} parameter
 * @returns {Boolean}
 */
window.isNumericParameter = function (parameter) {
  const baseParameter = window.getBaseParameter(parameter);

  if (!baseParameter) {
    return false;
  }

  return window.Laravel.erp.parameter_groups.numeric.includes(baseParameter);
};

/**
 * Check if parameter is option
 *
 * @param {String} parameter
 * @returns {Boolean}
 */
window.isOptionParameter = function (parameter) {
  return window.parameterType(parameter) == "option";
};

/**
 * Find the tax matching a province/state name.
 *
 * @param {String|null} name
 * @returns {Object|undefined}
 */
window.taxNameToTax = function (name) {
  if (typeof name !== "string" || name.trim() === "") {
    return undefined;
  }

  let key = name.toLowerCase();
  // convert accents to non-accents
  key = key.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  // only keep alpha characters
  key = key.replace(/[^a-zA-Z]/g, "");
  // trim spaces
  key = key.trim();

  const taxName = window.Laravel.provinces_taxes[key];
  const tax = window.Laravel.taxes.find(tax => tax.name === taxName);
  return tax;
};

/*
 |--------------------------------------------------------------------------
 | Production hours helpers
 |--------------------------------------------------------------------------
 */

/**
 * Get departments that have production hours
 *
 * @returns {Array}
 */
window.haveProductionMinutesDepartments = function () {
  return _.cloneDeep(Laravel.erp.departments).filter((department) => {
    return department !== "machining";
  });
};


export { uuid, convertCurrency };
